/**
 * Map WordPress community points into activity-browse pins
 * (same shape as curated europe-highlights for filtering + cards).
 */
import type {
  EuropeHighlight,
  HighlightActivity,
  HighlightFact,
} from "../types/europe-highlights";
import { WORLD_COUNTRIES } from "../data/world-countries";
import { resolvePointFeaturedImage } from "./point-card-image";

/** WP pointType / tag slug → activity browse lens. */
const POINT_TYPE_TO_ACTIVITY: Record<string, HighlightActivity> = {
  climbing: "climbing",
  "sport-climbing": "climbing",
  ferrata: "ferrata",
  rafting: "rafting",
  trail: "trail",
  hiking: "trail",
  paragliding: "paragliding",
  forest: "forest",
  mountain: "mountain",
  mountains: "mountain",
  lake: "lake",
  camping: "camping",
};

export type WpActivityPoint = {
  id: string;
  title: string;
  slug?: string;
  uri?: string;
  excerpt?: string | null;
  customAttributes?: { title?: string | null; value?: string | null }[] | null;
  pointType?: { nodes?: { slug?: string | null; name?: string | null }[] | null } | null;
  tags?: { nodes?: { slug?: string | null; name?: string | null }[] | null } | null;
  categories?: { nodes?: { slug?: string | null; name?: string | null }[] | null } | null;
  location?: {
    mapLatitude?: string | number | null;
    mapLongitude?: string | number | null;
  } | null;
  featuredImage?: {
    node?: { sourceUrl?: string | null; altText?: string | null } | null;
  } | null;
};

const COUNTRY_SLUGS = new Set(WORLD_COUNTRIES.map((c) => c.slug));

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pointSlugs(point: WpActivityPoint): string[] {
  const types = (point.pointType?.nodes || []).map((n) =>
    (n.slug || "").toLowerCase()
  );
  const tags = (point.tags?.nodes || []).map((n) => (n.slug || "").toLowerCase());
  return [...types, ...tags].filter(Boolean);
}

/** True when a climbing-typed point is actually a via ferrata / Klettersteig. */
export function isFerrataLikePoint(point: WpActivityPoint): boolean {
  const title = (point.title || "").toLowerCase();
  if (/ferrata|klettersteig|via\s*ferrata/.test(title)) return true;
  const slugs = pointSlugs(point);
  if (slugs.some((s) => s.includes("ferrata") || s.includes("klettersteig"))) {
    return true;
  }
  const attrs = point.customAttributes || [];
  const routes = attrs.find((a) => /^routes$/i.test(a.title || ""));
  const diff = attrs.find((a) => /^difficulty$/i.test(a.title || ""));
  const diffVal = (diff?.value || "").trim();
  // Letter / K-class only, no route count → ferrata grading
  if (
    !routes?.value &&
    diffVal &&
    /^(?:[a-f](?:\s*[\/–\-]\s*[a-f])?|k\s*[1-6](?:\s*[\/–\-]\s*k?\s*[1-6])?)$/i.test(
      diffVal
    )
  ) {
    return true;
  }
  return false;
}

export function pointMatchesActivity(
  point: WpActivityPoint,
  activity: HighlightActivity
): boolean {
  const slugs = pointSlugs(point);
  const mapped = new Set(
    slugs
      .map((s) => POINT_TYPE_TO_ACTIVITY[s])
      .filter(Boolean) as HighlightActivity[]
  );

  if (activity === "ferrata") {
    return mapped.has("ferrata") || isFerrataLikePoint(point);
  }
  if (activity === "climbing") {
    if (isFerrataLikePoint(point)) return false;
    return mapped.has("climbing");
  }
  return mapped.has(activity);
}

function countrySlugForPoint(point: WpActivityPoint): string {
  for (const c of point.categories?.nodes || []) {
    const slug = (c.slug || "").toLowerCase();
    if (COUNTRY_SLUGS.has(slug)) return slug;
  }
  // Nested region: climb to known country if parent not in query
  for (const c of point.categories?.nodes || []) {
    const slug = (c.slug || "").toLowerCase();
    if (slug && slug !== "uncategorized") return slug;
  }
  return "unknown";
}

function factsFromPoint(
  point: WpActivityPoint,
  activity: HighlightActivity
): HighlightFact[] {
  const attrs = point.customAttributes || [];
  const facts: HighlightFact[] = [];
  for (const a of attrs) {
    let key = (a.title || "").trim();
    const value = (a.value || "").trim();
    if (!key || !value) continue;
    if (activity === "climbing") {
      if (/^length$/i.test(key)) key = "Height";
      if (/^difficulty$/i.test(key)) key = "Grades";
    }
    if (activity === "ferrata") {
      if (/^total$/i.test(key)) key = "Duration";
      if (/^lenght$/i.test(key)) key = "Length";
      if (/^grade$/i.test(key)) key = "Difficulty";
    }
    facts.push({ key, value });
  }
  if (activity === "climbing") {
    const rank = (k: string) => {
      const t = k.toLowerCase();
      if (t === "routes") return 0;
      if (t === "grades") return 1;
      if (t === "height") return 2;
      return 10;
    };
    facts.sort((a, b) => rank(a.key) - rank(b.key));
  }
  if (activity === "ferrata") {
    const rank = (k: string) => {
      const t = k.toLowerCase();
      if (t === "difficulty") return 0;
      if (t === "length") return 1;
      if (t === "duration") return 2;
      return 10;
    };
    facts.sort((a, b) => rank(a.key) - rank(b.key));
  }
  return facts;
}

/** Convert a WP point into a browse/map highlight for one activity lens. */
export function wpPointAsHighlight(
  point: WpActivityPoint,
  activity: HighlightActivity
): EuropeHighlight | null {
  const lat = parseFloat(String(point.location?.mapLatitude ?? ""));
  const lng = parseFloat(String(point.location?.mapLongitude ?? ""));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const country = countrySlugForPoint(point);
  const summary = stripHtml(point.excerpt || "") || point.title;
  const image =
    resolvePointFeaturedImage(point.slug, point.featuredImage)?.node
      ?.sourceUrl || "";
  const typeSlugs = pointSlugs(point);
  const features = [
    ...typeSlugs.map((s) => s.replace(/-/g, " ")),
    "Community point",
  ];

  return {
    id: `wp-${point.id}`,
    slug: point.slug || point.id,
    name: point.title,
    summary,
    country,
    activities: [activity],
    primaryType: activity,
    features,
    facts: factsFromPoint(point, activity),
    image,
    tier: "priority",
    priority: 5,
    source: "wordpress",
    scale: "feature",
    verified: true,
    latitude: lat,
    longitude: lng,
    path: point.uri || `/point/${point.slug || ""}/`,
  };
}

/** All WP points that belong on an activity browse / map filter. */
export function getWpHighlightsForActivities(
  points: WpActivityPoint[],
  activities: HighlightActivity[]
): EuropeHighlight[] {
  const wanted = Array.from(new Set(activities.filter(Boolean)));
  if (!wanted.length) return [];
  const out: EuropeHighlight[] = [];
  const seen = new Set<string>();
  for (const activity of wanted) {
    for (const point of points) {
      if (!pointMatchesActivity(point, activity)) continue;
      const h = wpPointAsHighlight(point, activity);
      if (!h || seen.has(h.id)) continue;
      seen.add(h.id);
      out.push(h);
    }
  }
  return out;
}
